import { supabaseAdmin } from "./supabase";

interface PlayerSelectionParams {
  gameId: string;
  xpSlots: number;
  randomSlots: number;
}

interface PlayerStats {
  id: string;
  friendly_name?: string;
  xp: number;
  status?: string;
  selection_method?: string;
  whatsapp_group_member?: string;
  current_streak?: number;
  caps?: number;
  registration_time?: string;
  bench_warmer_streak?: number;
  using_token?: boolean;
  used_token_last_game?: boolean;
  /** Unexcused no-show in one of the last 3 games: bottom of the order (see get_no_show_penalised_players). */
  no_show_penalty?: boolean;
}

interface PlayerSelectionResult {
  success: boolean;
  selectedPlayers: PlayerStats[];
  nonSelectedPlayerIds: string[];
  error?: string;
}

export const handlePlayerSelection = async ({
  gameId,
  xpSlots,
  randomSlots,
}: PlayerSelectionParams): Promise<PlayerSelectionResult> => {
  try {
    // Get all registered players
    const { data: registeredPlayers, error: fetchError } = await supabaseAdmin
      .from('game_registrations')
      .select(`
        player_id,
        status,
        selection_method,
        created_at,
        using_token
      `)
      .eq('game_id', gameId)
      .eq('status', 'registered');

    if (fetchError) throw fetchError;
    if (!registeredPlayers?.length) return { success: false, error: 'No registered players found', selectedPlayers: [], nonSelectedPlayerIds: [] };

    // Get player IDs
    const playerIds = registeredPlayers.map(reg => reg.player_id);

    // First, get the previous game's token usage
    const { data: previousGameTokenUsers, error: tokenError } = await supabaseAdmin.rpc(
      'check_previous_game_token_usage',
      { current_game_id: gameId }
    );

    if (tokenError) {
      console.error('Error fetching previous game token usage:', tokenError);
      throw tokenError;
    }

    // Players on the no-show penalty (unexcused no-show in the last 3 games)
    const { data: noShowPenalised, error: noShowError } = await supabaseAdmin.rpc(
      'get_no_show_penalised_players',
      { p_game_id: gameId }
    );

    if (noShowError) {
      console.error('Error fetching no-show penalties:', noShowError);
      throw noShowError;
    }

    // Get player details including bench_warmer_streak
    const { data: playerDetails, error: playerError } = await supabaseAdmin
      .from('players')
      .select('id, friendly_name, whatsapp_group_member, bench_warmer_streak')
      .in('id', playerIds);

    if (playerError) throw playerError;

    // Get player stats
    const { data: playerStats, error: statsError } = await supabaseAdmin
      .from('player_stats')
      .select('id, xp, current_streak, caps')
      .in('id', playerIds);

    if (statsError) throw statsError;

    // Transform the data to include only what we need
    const players: PlayerStats[] = registeredPlayers.map(reg => {
      const details = playerDetails?.find(p => p.id === reg.player_id);
      const stats = playerStats?.find(p => p.id === reg.player_id);
      const usedTokenLastGame = previousGameTokenUsers?.some((u: { player_id: string }) => u.player_id === reg.player_id) ?? false;
      const noShowPenalty = noShowPenalised?.some((u: { player_id: string }) => u.player_id === reg.player_id) ?? false;
      return {
        id: reg.player_id,
        friendly_name: details?.friendly_name,
        xp: stats?.xp || 0,
        status: reg.status,
        selection_method: reg.selection_method,
        whatsapp_group_member: details?.whatsapp_group_member || 'No',
        current_streak: stats?.current_streak || 0,
        caps: stats?.caps || 0,
        registration_time: reg.created_at,
        bench_warmer_streak: details?.bench_warmer_streak || 0,
        using_token: reg.using_token || false,
        used_token_last_game: usedTokenLastGame,
        no_show_penalty: noShowPenalty
      };
    });

    // Sort remaining players by XP and tiebreakers (excluding token users)
    const remainingPlayers = players.filter(player => !player.using_token);
    const sortedPlayers = [...remainingPlayers].sort((a, b) => {
      // No-show penalty players go to the very bottom (below token cooldown)
      if (!!a.no_show_penalty !== !!b.no_show_penalty) {
        return a.no_show_penalty ? 1 : -1;
      }

      // Then check if either player used a token in the previous game
      const aUsedTokenPreviously = previousGameTokenUsers?.some((u: { player_id: string }) => u.player_id === a.id) ?? false;
      const bUsedTokenPreviously = previousGameTokenUsers?.some((u: { player_id: string }) => u.player_id === b.id) ?? false;

      // If either used a token in the previous game, they go to the bottom
      if (aUsedTokenPreviously !== bUsedTokenPreviously) {
        return aUsedTokenPreviously ? 1 : -1;
      }

      // If both used tokens in previous game, or neither did, use normal sorting
      // First compare by XP
      if (b.xp !== a.xp) {
        return b.xp - a.xp;
      }

      // If XP is equal, then check WhatsApp status
      const aIsWhatsApp = a.whatsapp_group_member === 'Yes' || a.whatsapp_group_member === 'Proxy';
      const bIsWhatsApp = b.whatsapp_group_member === 'Yes' || b.whatsapp_group_member === 'Proxy';
      
      if (aIsWhatsApp !== bIsWhatsApp) {
        return aIsWhatsApp ? -1 : 1;
      }

      // Both have same XP and WhatsApp status - check streak
      if (b.current_streak !== a.current_streak) {
        return (b.current_streak || 0) - (a.current_streak || 0);
      }

      // Same streak - check caps
      if (b.caps !== a.caps) {
        return (b.caps || 0) - (a.caps || 0);
      }

      // Same caps - check registration time
      return (a.registration_time || '').localeCompare(b.registration_time || '');
    });

    // Now check which token users would still get in by merit AFTER considering token effects
    const allPlayersWithTokenEffects = [...players].sort((a, b) => {
      // First compare by XP
      if (b.xp !== a.xp) {
        return b.xp - a.xp;
      }

      // If XP is equal, then check WhatsApp status
      const aIsWhatsApp = a.whatsapp_group_member === 'Yes' || a.whatsapp_group_member === 'Proxy';
      const bIsWhatsApp = b.whatsapp_group_member === 'Yes' || b.whatsapp_group_member === 'Proxy';
      
      if (aIsWhatsApp !== bIsWhatsApp) {
        return aIsWhatsApp ? -1 : 1;
      }

      // Both have same XP and WhatsApp status - check streak
      if (b.current_streak !== a.current_streak) {
        return (b.current_streak || 0) - (a.current_streak || 0);
      }

      // Same streak - check caps
      if (b.caps !== a.caps) {
        return (b.caps || 0) - (a.caps || 0);
      }

      // Same caps - check registration time
      return (a.registration_time || '').localeCompare(b.registration_time || '');
    });

    // Get who would get in by merit after token effects
    // Note: This calculation was previously used but is now handled directly when selecting players

    // Now check which token users would still get in by merit
    const tokenUsersSelectedByMerit = players.filter(player => player.using_token).filter(tokenPlayer => {
      // Find their position in the merit-based list (including token effects)
      const meritPosition = allPlayersWithTokenEffects.findIndex(p => p.id === tokenPlayer.id);
      // They would get in by merit if their position is within the original xpSlots
      return meritPosition < xpSlots;
    });

    // Select players by XP for remaining XP slots
    const xpSelectedPlayers = sortedPlayers.slice(0, Math.max(0, xpSlots - players.filter(player => player.using_token).length));
    
    // The remaining players should be everyone NOT selected by XP or token
    const playersForRandomSelection = sortedPlayers.filter(player => 
      !xpSelectedPlayers.some(xpPlayer => xpPlayer.id === player.id)
    );

    // Handle random selection with WhatsApp priority and weighted probabilities
    // Tiers: WhatsApp-eligible -> non-WhatsApp-eligible -> WhatsApp on token
    // cooldown -> WhatsApp on no-show penalty. Non-WhatsApp players on either
    // cooldown or penalty are never drawn. The penalty wins over the cooldown.
    const isWhatsApp = (p: PlayerStats) => p.whatsapp_group_member === 'Yes' || p.whatsapp_group_member === 'Proxy';
    const noShowPenaltyPlayers = playersForRandomSelection.filter(p => p.no_show_penalty && isWhatsApp(p));
    const tokenCooldownPlayers = playersForRandomSelection.filter(
      p => !p.no_show_penalty && p.used_token_last_game && isWhatsApp(p)
    );
    const eligiblePlayers = playersForRandomSelection.filter(p => !p.used_token_last_game && !p.no_show_penalty);
    
    // Split eligible players by WhatsApp status
    const whatsappMembers = eligiblePlayers.filter(
      p => p.whatsapp_group_member === 'Yes' || p.whatsapp_group_member === 'Proxy'
    );
    
    let randomSelectedPlayers: PlayerStats[] = [];
    
    // Helper function to select players using weighted probabilities
    const selectWithWeights = (candidates: PlayerStats[], numSlots: number): PlayerStats[] => {
      const selected: PlayerStats[] = [];
      const remainingCandidates = [...candidates];
      
      for (let i = 0; i < numSlots && remainingCandidates.length > 0; i++) {
        // Calculate weights for remaining candidates
        const weights = remainingCandidates.map(p => 1 + (p.bench_warmer_streak || 0));
        const totalWeight = weights.reduce((sum, w) => sum + w, 0);
        
        // Create cumulative ranges
        const ranges = weights.map((weight, index) => {
          const start = index === 0 ? 0 : 
            weights.slice(0, index).reduce((sum, w) => sum + w, 0);
          return {
            player: remainingCandidates[index],
            start,
            end: start + weight
          };
        });
        
        // Select a player
        const roll = Math.random() * totalWeight;
        const selectedRange = ranges.find(r => roll >= r.start && roll < r.end);
        
        if (selectedRange) {
          selected.push(selectedRange.player);
          // Remove selected player from remaining candidates
          const selectedIndex = remainingCandidates.findIndex(p => p.id === selectedRange.player.id);
          if (selectedIndex !== -1) {
            remainingCandidates.splice(selectedIndex, 1);
          }
        }
      }
      
      return selected;
    };
    
    if (whatsappMembers.length >= randomSlots) {
      // If we have enough WhatsApp members, select only from them using weights
      randomSelectedPlayers = selectWithWeights(whatsappMembers, randomSlots);
    } else {
      // Fill with all WhatsApp members first
      randomSelectedPlayers = [...whatsappMembers];
      
      // Fill remaining slots from non-WhatsApp members using weights
      const nonWhatsappMembers = eligiblePlayers.filter(
        p => p.whatsapp_group_member === 'No' || p.whatsapp_group_member === null
      );
      
      const remainingSlots = randomSlots - whatsappMembers.length;
      
      // If we still need more players after using all eligible players
      if (nonWhatsappMembers.length >= remainingSlots) {
        // We have enough non-WhatsApp members
        const additionalPlayers = selectWithWeights(nonWhatsappMembers, remainingSlots);
        randomSelectedPlayers = [...randomSelectedPlayers, ...additionalPlayers];
      } else {
        // Add all available non-WhatsApp members first
        randomSelectedPlayers = [...randomSelectedPlayers, ...nonWhatsappMembers];
        
        // If we still need more players, only then consider WhatsApp members on token cooldown
        const stillNeeded = randomSlots - randomSelectedPlayers.length;
        if (stillNeeded > 0 && tokenCooldownPlayers.length > 0) {
          const cooldownPlayers = selectWithWeights(tokenCooldownPlayers, stillNeeded);
          randomSelectedPlayers = [...randomSelectedPlayers, ...cooldownPlayers];
        }

        // Last of all, WhatsApp members on the no-show penalty
        const stillNeededAfterCooldown = randomSlots - randomSelectedPlayers.length;
        if (stillNeededAfterCooldown > 0 && noShowPenaltyPlayers.length > 0) {
          const penalisedPlayers = selectWithWeights(noShowPenaltyPlayers, stillNeededAfterCooldown);
          randomSelectedPlayers = [...randomSelectedPlayers, ...penalisedPlayers];
        }
      }
    }

    // Combine all selected players
    const selectedPlayers = [
      ...players.filter(player => player.using_token).map(player => {
        const wouldGetInByMerit = tokenUsersSelectedByMerit.some(p => p.id === player.id);
        return {
          ...player,
          selection_method: wouldGetInByMerit ? 'merit' : 'token',
          using_token: !wouldGetInByMerit // Only consume token if they wouldn't get in by merit
        };
      }),
      ...xpSelectedPlayers.map(player => ({
        ...player,
        selection_method: 'merit'
      })),
      ...randomSelectedPlayers.map(player => ({
        ...player,
        selection_method: 'random'
      }))
    ];

    // Helper function to update player status with retry
    const updatePlayerStatus = async (
      playerId: string,
      status: 'selected' | 'reserve',
      selectionMethod: 'token' | 'merit' | 'random' | 'none',
      usingToken?: boolean
    ) => {
      try {
        console.log(`Updating player ${playerId} to status: ${status}, method: ${selectionMethod}, usingToken: ${usingToken}`);

        // Use the SECURITY DEFINER RPC function to update the registration
        // Bypass permission check since this is an automated system process
        const { data, error } = await supabaseAdmin.rpc('update_game_registration', {
          p_game_id: gameId,
          p_player_id: playerId,
          p_status: status,
          p_selection_method: selectionMethod,
          p_using_token: usingToken !== undefined ? usingToken : null,
          p_bypass_permission: true
        });

        if (error) {
          console.error(`Error updating player ${playerId}:`, {
            error,
            details: error.details,
            hint: error.hint,
            message: error.message
          });
          throw error;
        }

        console.log(`Successfully updated player ${playerId} via RPC`, data);
        return data;
      } catch (error: any) {
        console.error(`Failed to update player ${playerId}:`, {
          error,
          message: error.message,
          stack: error.stack
        });
        throw error;
      }
    };

    try {
      console.log('Starting player selection process...');
      console.log('Token players:', players.filter(player => player.using_token).length);
      console.log('XP players:', xpSelectedPlayers.length);
      console.log('Random players:', randomSelectedPlayers.length);

      // Update token players first
      for (const player of players.filter(player => player.using_token)) {
        const wouldGetInByMerit = tokenUsersSelectedByMerit.some(p => p.id === player.id);
        await updatePlayerStatus(
          player.id,
          'selected',
          wouldGetInByMerit ? 'merit' : 'token',
          !wouldGetInByMerit // Only keep using_token=true if they wouldn't get in by merit
        );
      }

      // Update merit-based selections
      for (const player of xpSelectedPlayers) {
        await updatePlayerStatus(player.id, 'selected', 'merit');
      }

      // Update random selections
      for (const player of randomSelectedPlayers) {
        await updatePlayerStatus(player.id, 'selected', 'random');
      }

      // Update non-selected players to reserve
      const nonSelectedPlayerIds = players
        .filter(p => !selectedPlayers.find(sp => sp.id === p.id))
        .map(p => p.id);

      console.log('Non-selected players:', nonSelectedPlayerIds.length);

      for (const playerId of nonSelectedPlayerIds) {
        await updatePlayerStatus(playerId, 'reserve', 'none');
      }

      return {
        success: true,
        selectedPlayers,
        nonSelectedPlayerIds
      };
    } catch (error: any) {
      console.error('Error in player selection:', {
        error,
        message: error.message,
        stack: error.stack,
        context: {
          tokenPlayers: players.filter(player => player.using_token).length,
          xpPlayers: xpSelectedPlayers.length,
          randomPlayers: randomSelectedPlayers.length
        }
      });
      return {
        success: false,
        error: `Failed to update player statuses: ${error.message}`,
        selectedPlayers: [],
        nonSelectedPlayerIds: []
      };
    }
  } catch (error) {
    return {
      success: false,
      selectedPlayers: [],
      nonSelectedPlayerIds: [],
      error: error instanceof Error ? error.message : 'An error occurred during player selection'
    };
  }
};
